import { paths } from './paths';

// ----------------------------------------------------------------------

export type PageMeta = {
  title: string;
  description?: string;
};

/**
 * Maps pathname prefixes (same string values as `paths`) to a title and an optional
 * meta description. Resolution uses longest-prefix match on normalized pathname.
 * A description is emitted only for entries that set one.
 */
export const PATH_TITLE_MAP: Record<string, PageMeta> = {
  [paths.dashboard.root]: { title: 'Dashboard' },

  [paths.gettingStarted.root]: { title: 'Getting started' },

  [paths.jobs.root]: { title: 'Job Board' },
  [paths.jobs.jobDetails]: { title: 'Job details' },
  [paths.jobs.editorSample]: { title: 'Editor sample' },
  [paths.jobs.dashboard]: { title: 'Job dashboard' },
  [paths.jobs.candidates]: { title: 'Candidates' },
  [paths.jobs.workflow]: { title: 'Workflow' },
  [paths.jobs.reports]: { title: 'Job reports' },
  [paths.jobs.createJob]: { title: 'Create job' },
  [paths.jobs.interviewHub]: { title: 'Interview hub' },
  [paths.jobs.interviewAgenda]: { title: 'Interview agenda' },
  [paths.jobs.interviewEvaluation]: { title: 'Interview evaluation' },
  [paths.jobs.compare]: { title: 'Compare' },

  [paths.createJob.root]: { title: 'Create job' },

  [paths.candidateEvidenceView.root]: { title: 'Resume' },

  [paths.candidates.root]: { title: 'Candidates' },
  '/candidates/details': { title: 'Candidate Detail' },
  '/candidates/new-details': { title: 'Candidate Detail' },

  [paths.orgSettings.root]: { title: 'Organization' },
  [paths.orgSettings.info]: { title: 'Company info' },
  [paths.orgSettings.jobBoardEmbed]: { title: 'Job board embed' },
  [paths.orgSettings.integrations]: { title: 'Integrations' },
  [paths.orgSettings.billing]: { title: 'Billing & plans' },
  [paths.orgSettings.members]: { title: 'Members' },
  [paths.orgSettings.invitations]: { title: 'Invitations' },
  [paths.orgSettings.knowledgeBase]: { title: 'Knowledge base' },
  [paths.orgSettings.hiringTeam]: { title: 'Hiring team' },
  [paths.orgSettings.agentBuilder]: { title: 'Agent builder' },
  [paths.orgSettings.formTemplates]: { title: 'Form templates' },
  [paths.orgSettings.notifications.emailTemplates]: { title: 'Email templates' },
  [paths.orgSettings.notifications.root]: { title: 'Notifications' },

  [`${paths.orgSettings.billing}/success`]: { title: 'Billing success' },
  [`${paths.orgSettings.billing}/portal-return`]: { title: 'Billing portal' },

  [paths.billing.success]: { title: 'Billing success' },
  [paths.billing.cancel]: { title: 'Billing canceled' },
  [paths.billing.portalReturn]: { title: 'Billing portal' },

  [paths.integrations.callback]: { title: 'Connecting integration' },

  [paths.eventsTrial]: { title: 'Events' },

  [paths.auth.auth0.signIn]: {
    title: 'Sign in',
    description:
      'Sign up or log in to Hello Recruiter to manage your jobs, review AI interview results, and track every candidate in one place',
  },
  [paths.auth.auth0.verifyEmail]: { title: 'Verify email' },
  [paths.auth.auth0.onBoarding]: { title: 'Onboarding' },
  '/auth/callback': { title: 'Signing in' },

  '/accept-invitation': { title: 'Invitation' },

  [paths.newCandidates.root]: { title: 'Candidates' },
  [paths.newCandidates.candidateDetails]: { title: 'Candidate' },

  // Future / optional routes from paths (not all are mounted today)
  [paths.auth.jwt.signIn]: { title: 'Sign in' },
  [paths.auth.jwt.signUp]: { title: 'Sign up' },
};

/** Descending length — longest prefix wins */
const SORTED_PATH_KEYS = Object.keys(PATH_TITLE_MAP).sort((a, b) => b.length - a.length);

// ----------------------------------------------------------------------

export function normalizePathname(pathname: string): string {
  if (!pathname) {
    return '/';
  }
  let p = pathname.trim().toLowerCase();
  if (p !== '/') {
    p = p.replace(/\/+$/, '');
  }
  return p || '/';
}

function findPageMeta(pathname: string): PageMeta | undefined {
  const norm = normalizePathname(pathname);

  for (const key of SORTED_PATH_KEYS) {
    const prefix = normalizePathname(key);
    if (norm === prefix || (prefix !== '/' && norm.startsWith(`${prefix}/`))) {
      return PATH_TITLE_MAP[key];
    }
  }

  return undefined;
}

export function getPageTitleSegment(pathname: string): string | undefined {
  return findPageMeta(pathname)?.title;
}

export function getPageDescription(pathname: string): string | undefined {
  return findPageMeta(pathname)?.description;
}

export function formatPageTitle(segment: string, appName: string): string {
  return `${segment} - ${appName}`;
}

export function getPageTitle(pathname: string, appName: string, fallbackSegment = 'Page'): string {
  const segment = getPageTitleSegment(pathname) ?? fallbackSegment;
  return formatPageTitle(segment, appName);
}
